import { MaybeRef } from '@vueuse/core';
import { parse, serialize } from 'cookie-es';
import { destr } from 'destr';
import isEqual from 'fast-deep-equal/es6';
import { nextTick, ref, toRaw, watch } from 'vue';

import isNullOrUndefined from '@/utils/isNullOrUndefined';

import type { CookieParseOptions, CookieSerializeOptions } from 'cookie-es';
import type { Ref } from 'vue';

type CookieOptions<T = any> = Override<
  CookieSerializeOptions & CookieParseOptions,
  {
    decode?(value: string): T;
    encode?(value: T): string;
  }
>;

type UseCookieOptions<T = string | null | undefined> = Override<
  CookieOptions<T>,
  {
    default?: () => MaybeRef<T>;
    watch?: boolean | 'shallow';
  }
>;

const USE_COOKIE_OPTIONS_DEFAULT: UseCookieOptions<any> = {
  path: '/',
  watch: true,
  decode: val => destr(decodeURIComponent(val)),
  encode: val =>
    encodeURIComponent(typeof val === 'string' ? val : JSON.stringify(val)),
};

export default <T = string | null | undefined>(
  name: string,
  options?: UseCookieOptions<T>
): Ref<T> => {
  const channel = !(typeof BroadcastChannel === 'undefined')
    ? new BroadcastChannel(`cookies:${name}`)
    : null;

  const opts = { ...USE_COOKIE_OPTIONS_DEFAULT, ...options };
  const cookies = readRawCookies(opts) || {};

  const cookie = ref<T | undefined>((cookies[name] as any) ?? opts.default?.());

  const callback = () => {
    writeClientCookie(name, cookie.value, opts as CookieSerializeOptions);

    channel?.postMessage(toRaw(cookie.value));
  };

  let watchPaused = false;

  if (channel) {
    channel.addEventListener('message', event => {
      watchPaused = true;
      cookie.value = event.data;
      nextTick(() => (watchPaused = false));
    });
  }

  if (opts.watch) {
    watch(
      cookie,
      (newVal, oldVal) => {
        if (watchPaused || isEqual(newVal, oldVal)) return;
        callback();
      },
      { deep: opts.watch !== 'shallow' }
    );
  } else {
    callback();
  }

  return cookie as Ref<T>;
};

export function readRawCookies(
  options: CookieOptions = {}
): Record<string, any> | undefined {
  return parse(document.cookie, options);
}

function serializeCookie(
  name: string,
  value: any,
  opts: CookieSerializeOptions = {}
) {
  return serialize(
    name,
    value,
    isNullOrUndefined(value) ? { ...opts, maxAge: -1 } : opts
  );
}

export function writeClientCookie(
  name: string,
  value: any,
  opts: CookieSerializeOptions = {}
) {
  document.cookie = serializeCookie(name, value, opts);
}
